<template>
  <div
    :class="{
      'filters-sidebar': true,
      'filters-sidebar--sticky': !isModalOpen,
    }"
  >
    <AisCurrentRefinements
      v-if="!hideModalButton"
      :excluded-attributes="['catalog_hierarchical_categories.v3.lvl0', 'query']"
    >
      <template #default="{ items }">
        <AkButton
          class="filters-sidebar__modal-button"
          data-testid="filters-sidebar__mobile-button"
          color="white"
          :outlined="true"
          @click="openModal"
        >
          <AkPill
            class="ds-mr-2"
            :color="AkPillColor.Success100"
            :text-color="AkPillColor.Accent"
            active
            :content="getFiltersNumber(items)"
          />
          {{ mobileButtonLabel }}
        </AkButton>
      </template>
    </AisCurrentRefinements>
    <div
      :class="{
        'filters-sidebar__container': true,
        'filters-sidebar__container--open': isModalOpen,
      }"
    >
      <div class="filters-sidebar__heading">
        <AkHeading3 class="filters-sidebar__title">
          {{ mobileHeading }}
        </AkHeading3>
        <AkButton
          class="filters-sidebar__chevron"
          data-testid="filters-sidebar__close-button"
          color="white"
          size="lg"
          symbol="x"
          @click="closeModal"
        />
      </div>
      <div class="filters-sidebar__content">
        <slot />
      </div>
      <div class="filters-sidebar__footer">
        <AkButton
          class="filters-sidebar__apply-button"
          data-testid="filters-sidebar__apply-button"
          @click="closeModal"
        >
          <AisStats>
            <template #default="{ nbHits }">
              {{ $t('discovery.filters.filtersModal.applyButton', { nbHits: nbHits }) }}
            </template>
          </AisStats>
        </AkButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { AkButton, AkHeading3, AkPill, AkPillColor } from '@ankorstore/design-system';
import { AisCurrentRefinements, AisStats } from 'vue-instantsearch/vue3/es';

export default defineComponent({
  name: 'FiltersSidebar',
  components: {
    AkPill,
    AkButton,
    AkHeading3,
    AisCurrentRefinements,
    AisStats,
  },
  props: {
    mobileButtonLabel: {
      type: String,
      required: true,
    },
    mobileHeading: {
      type: String,
      required: true,
    },
    hideModalButton: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const isModalOpen = ref(false);
    const openModal = () => {
      isModalOpen.value = true;
    };
    const closeModal = () => {
      isModalOpen.value = false;
    };

    const getFiltersNumber = (items) => {
      const totalRefinements = items.reduce((total, item) => {
        return total + (item.refinements ? item.refinements.length : 0);
      }, 0);
      return totalRefinements;
    };

    return {
      isModalOpen,
      openModal,
      closeModal,
      getFiltersNumber,
      AkPillColor,
    };
  },
});
</script>

<style lang="scss" scoped>
@use '@css/abstracts/design-tokens' as d;
.filters-sidebar {
  // block
  background-color: theme('colors.white');
  z-index: 10;

  // element
  & &__modal-button {
    @apply ds-mt-4;
    width: 100%;
    margin-bottom: 1.5rem;
    @include d.lg() {
      display: none;
    }
  }

  &__container {
    display: none;
    z-index: 1000;
    background-color: theme('colors.white');
    @include d.lg() {
      display: block;
    }
    &--open {
      display: block;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  }

  &__heading {
    @apply ds-mb-4;
    position: sticky;
    top: 0;
    background-color: rgba(255, 255, 255, 1);
    z-index: 100;
    padding: 1rem 0 0.75rem;
    border-bottom: 1px solid theme('colors.neutral.300');
    @include d.lg() {
      display: none;
    }
  }

  &__title {
    width: 100%;
    text-align: center;
  }

  & &__chevron {
    position: absolute;
    height: 100%;
    top: 0;
    right: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    @include d.lg() {
      padding: 0;
    }
  }

  &__footer {
    padding: 1rem 1rem 1.5rem;
    position: sticky;
    bottom: 0;
    background-color: rgba(255, 255, 255, 1);
    @include d.lg() {
      display: none;
    }
  }

  &__apply-button {
    width: 100%;
  }

  // modifier
  &--sticky {
    position: sticky;
  }
}
</style>
